import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { memo, useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { IoClose, IoRefresh } from 'react-icons/io5'

import {
  SubjectAutocomplete,
  TagAutocomplete,
} from '@/pages/Exercises/NewQuestion/modules'
import type { Question, QuestionTag, QuestionUpdatePayload } from '@/types'

import { Quill } from '..'

interface QuestionEditFormProps {
  open: boolean
  question: (Question & { description: string }) | null
  onSubmit: (data: QuestionUpdatePayload) => Promise<void>
  onClose: () => void
  isLoading: boolean
  isLoadingDetails: boolean
  fetchError: string
  onRetry: () => void
}

const difficultyOptions = [
  { value: 'easy', label: 'Fácil' },
  { value: 'intermediary', label: 'Médio' },
  { value: 'hard', label: 'Difícil' },
] as const

const QuestionEditForm = memo<QuestionEditFormProps>(
  ({
    open,
    question,
    onSubmit,
    onClose,
    isLoading,
    isLoadingDetails,
    fetchError,
    onRetry,
  }) => {
    const [submitError, setSubmitError] = useState<string>('')
    const [competenceTags, setCompetenceTags] = useState<QuestionTag[]>([])
    const [abilityTags, setAbilityTags] = useState<QuestionTag[]>([])
    const [subjectTags, setSubjectTags] = useState<QuestionTag[]>([])
    const [contentTags, setContentTags] = useState<QuestionTag[]>([])

    const [initialTags, setInitialTags] = useState({
      competence: [] as QuestionTag[],
      ability: [] as QuestionTag[],
      subject: [] as QuestionTag[],
      content: [] as QuestionTag[],
    })

    const {
      control,
      handleSubmit,
      formState: { errors, isDirty },
      reset,
    } = useForm<QuestionUpdatePayload>({
      defaultValues: {
        id: 0,
        question: '',
        description: '',
        difficulty: 'easy',
        subject_tags: [],
        ability_tags: [],
        content_tags: [],
        competence_tags: [],
      },
    })

    useEffect(() => {
      if (question) {
        const tagsByType = question.tags.reduce(
          (acc, tag) => {
            const type = tag.type || 'other'
            if (!acc[type]) {
              acc[type] = []
            }
            acc[type].push(tag)
            return acc
          },
          {} as Record<string, QuestionTag[]>,
        )
        const tags = {
          competence: tagsByType.competence || [],
          ability: tagsByType.ability || [],
          subject: tagsByType.subject || [],
          content: tagsByType.content || [],
        }

        setCompetenceTags(tags.competence || [])
        setAbilityTags(tags.ability || [])
        setSubjectTags(tags.subject || [])
        setContentTags(tags.content || [])
        setInitialTags(tags)

        reset({
          id: question.id,
          question: question.question,
          description: question.description,
          difficulty: question.difficulty,
          subject_tags: tags.subject || [],
          ability_tags: tags.ability || [],
          content_tags: tags.content || [],
          competence_tags: tags.competence || [],
        })
      }
    }, [question, reset])

    const handleFormSubmit = useCallback(
      async (data: QuestionUpdatePayload) => {
        setSubmitError('')

        const payload: QuestionUpdatePayload = {
          ...data,
          ability_tags: abilityTags,
          content_tags: contentTags,
          subject_tags: subjectTags,
          competence_tags: competenceTags,
        }

        try {
          await onSubmit(payload)
          reset()
        } catch (error) {
          setSubmitError(`Erro ao atualizar questão: ${error}`)
        }
      },
      [onSubmit, reset, abilityTags, contentTags, subjectTags, competenceTags],
    )

    const tagsHaveChanged = useCallback(() => {
      const arraysEqual = (a: QuestionTag[], b: QuestionTag[]) =>
        a.length === b.length && a.every((item, index) => item.id === b[index]?.id)

      return (
        !arraysEqual(competenceTags, initialTags.competence) ||
        !arraysEqual(abilityTags, initialTags.ability) ||
        !arraysEqual(subjectTags, initialTags.subject) ||
        !arraysEqual(contentTags, initialTags.content)
      )
    }, [competenceTags, abilityTags, subjectTags, contentTags, initialTags])

    const handleClose = useCallback(() => {
      reset()
      setSubmitError('')
      setCompetenceTags([])
      setAbilityTags([])
      setSubjectTags([])
      setContentTags([])
      onClose()
    }, [onClose, reset])

    const renderLoadingContent = () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          gap: 3,
        }}>
        <CircularProgress size={48} />
        <Typography variant="h6" color="text.secondary">
          Carregando dados da questão...
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Por favor, aguarde enquanto buscamos as informações da questão.
        </Typography>
      </Box>
    )

    const renderErrorContent = () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          gap: 3,
        }}>
        <Alert severity="error" sx={{ width: '100%', borderRadius: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Erro ao carregar questão</strong>
          </Typography>
          <Typography variant="body2">{fetchError}</Typography>
        </Alert>
        <Button
          variant="contained"
          startIcon={<IoRefresh />}
          onClick={onRetry}
          sx={{ borderRadius: 2 }}>
          Tentar Novamente
        </Button>
      </Box>
    )

    const isFormDirty = isDirty || tagsHaveChanged()

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              maxHeight: '90vh',
            },
          },
        }}>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Editar Questão
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={isLoading}
            sx={{ color: 'text.secondary' }}>
            <IoClose />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 3 }}>
          {isLoadingDetails ? (
            renderLoadingContent()
          ) : fetchError ? (
            renderErrorContent()
          ) : question ? (
            <form onSubmit={handleSubmit(handleFormSubmit)}>
              <Stack spacing={3}>
                <Alert variant="outlined" severity="warning" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2">
                    <strong>Atenção:</strong> Só é possível editar uma questão que ainda
                    não está sendo usada em uma avaliação!
                  </Typography>
                </Alert>

                {submitError && (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {submitError}
                  </Alert>
                )}

                <Controller
                  name="question"
                  control={control}
                  rules={{ required: 'Título da questão é obrigatório' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Título da Questão"
                      multiline
                      rows={3}
                      error={!!errors.question}
                      helperText={errors.question?.message}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />

                <FormControl>
                  <FormLabel sx={{ mb: 1, fontWeight: 600 }}>
                    Descrição da Questão
                  </FormLabel>
                  <Controller
                    name="description"
                    control={control}
                    rules={{ required: 'Descrição é obrigatória' }}
                    render={({ field }) => (
                      <Quill value={field.value} onChange={field.onChange} />
                    )}
                  />
                </FormControl>

                <Controller
                  name="difficulty"
                  control={control}
                  rules={{ required: 'Dificuldade é obrigatória' }}
                  render={({ field }) => (
                    <FormControl error={!!errors.difficulty}>
                      <FormLabel sx={{ mb: 1, fontWeight: 600 }}>Dificuldade</FormLabel>
                      <Select {...field} displayEmpty sx={{ borderRadius: 2 }}>
                        <MenuItem value="" disabled>
                          Selecione a dificuldade
                        </MenuItem>
                        {difficultyOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />

                <Stack spacing={{ xs: 2, sm: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: 'primary.main',
                      textAlign: { xs: 'center', sm: 'left' },
                    }}>
                    Tags e Categorização
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      textAlign: { xs: 'center', sm: 'left' },
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    }}>
                    Adicione tags para categorizar sua questão (opcional)
                  </Typography>

                  <Alert severity="info" variant="outlined">
                    Para adicionar a tag, pressione Enter após digitar o nome
                    corretamente.
                  </Alert>

                  <Stack spacing={{ xs: 2, sm: 2 }}>
                    <TagAutocomplete
                      value={competenceTags}
                      setValue={setCompetenceTags}
                      placeholder="Adicionar competências..."
                      label="Competências"
                    />
                    <TagAutocomplete
                      value={abilityTags}
                      setValue={setAbilityTags}
                      placeholder="Adicionar habilidades..."
                      label="Habilidades"
                    />
                    <SubjectAutocomplete
                      subjectTags={subjectTags}
                      setSubjectTags={setSubjectTags}
                    />
                    <TagAutocomplete
                      value={contentTags}
                      setValue={setContentTags}
                      placeholder="Adicionar conteúdos..."
                      label="Conteúdos"
                    />
                  </Stack>
                </Stack>
              </Stack>
            </form>
          ) : null}
        </DialogContent>

        {!isLoadingDetails && !fetchError && question && (
          <>
            <Divider />
            <DialogActions sx={{ p: 3, gap: 2 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isLoading}
                disabled={!isFormDirty}
                onClick={handleSubmit(handleFormSubmit)}
                sx={{
                  minWidth: 120,
                  borderRadius: 2,
                }}>
                {isLoading ? 'Salvando...' : 'Salvar Alterações'}
              </LoadingButton>

              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isLoading}
                sx={{
                  minWidth: 120,
                  borderRadius: 2,
                }}>
                Cancelar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    )
  },
)

QuestionEditForm.displayName = 'QuestionEditForm'

export default QuestionEditForm
